import React from 'react';
import { renderToString } from 'react-dom/server';
import { Check, Minus, X } from 'react-feather';
import styled, { css } from 'styled-components';
import { Label, Variant } from '~/components/atom/Typography';

export type Props = {
  onBlur?: (arg0: React.FocusEvent<any> | null | undefined) => void;
  onChange?: (arg0: React.SyntheticEvent<any> | any) => void;
  onFocus?: (arg0: React.FocusEvent<any> | null | undefined) => void;

  name?: string;

  /** Label to be displayed, will also act as placeholder if nothing is entered. */
  label?: React.ReactNode;

  /** Error, this replaces the label if its set. */
  error?: string | null | undefined;
  value: boolean;
  className?: string;

  /** Custom label component that can be used instead of the label */
  labelComponent?: React.ReactNode;

  /** Icon to be shown in the checkbox when it is checked */
  icon?: 'check' | 'minus' | 'cross';

  /** Disables the checkbox */
  disabled?: boolean;

  /** Makes the checkbox larger */
  large?: boolean;

  /** Shows empty checkbox with primary background */
  isFocused?: boolean;

  /** Appearance of the checkbox when it is checked */
  appearance?: 'danger' | 'success' | 'primary' | 'secondary';
};

const Checkbox: React.FCC<Props> = ({
  label,
  error,
  value,
  className,
  labelComponent,
  onChange,
  dataTestId,
  icon = 'check',
  isFocused,
  large,
  disabled,
  appearance,
}) => {
  const svgProps = {
    color: 'rgb(255,255,255)',
    height: large ? '1.1em' : '0.95em',
    width: large ? '1.1em' : '0.95em',
  };

  const checkIconStr = renderToString(<Check {...svgProps} />);
  const minusIconStr = renderToString(<Minus {...svgProps} />);
  const crossIconStr = renderToString(<X {...svgProps} />);

  const iconShown = (() => {
    if (icon === 'minus') return minusIconStr;
    if (icon === 'cross') return crossIconStr;
    return checkIconStr;
  })();

  return (
    <Container
      $error={error}
      className={className}
      onClick={e => e.stopPropagation()}
      $disabled={disabled}
    >
      <CheckBoxElement
        type="checkbox"
        value={value.toString()}
        checked={value}
        onChange={e => {
          /**
           *  This is a fix for https://bugzilla.mozilla.org/show_bug.cgi?id=62151.
           *  We prevent the default behaviour of any parent of checkbox element while still enabling checkbox to be checked
           */
          e && e.preventDefault();
          setTimeout(() => onChange && onChange(e), 0);
        }}
        data-testid={dataTestId}
        $isFocused={isFocused}
        $large={large}
        disabled={disabled}
        $appearance={appearance}
        $iconShown={iconShown}
      />
      &nbsp;
      <Label
        margin={[null]}
        size="base"
        fontWeight="regular"
        variant={Variant.primary}
      >
        {error || label || labelComponent}
      </Label>
    </Container>
  );
};

type LabelProps = {
  $error?: string | null;
  $disabled?: boolean;
};
const Container = styled.label<LabelProps>`
  display: flex;
  align-items: center;
  width: 100%;

  ${({ theme, $error, $disabled }) => {
    if ($error) {
      return css`
        color: ${theme.color('danger')};
      `;
    }
    if ($disabled) {
      return css`
        color: ${theme.color('tertiary', 'dark')};
      `;
    }

    return null;
  }};
`;

type CheckboxProps = {
  $isFocused?: boolean;
  $large?: boolean;
  $iconShown: string;
  $appearance?: Props['appearance'];
};

const CheckBoxElement = styled.input<CheckboxProps>`
  margin-right: 0.6em;
  cursor: pointer;
  position: relative;
  appearance: none;
  box-sizing: border-box;

  ${({ theme, $isFocused, $large, $iconShown, $appearance }) => {
    const size = $large ? '1.5rem' : '1.2rem';

    return css`
      border: ${theme.getTokens().border.width.s} solid
        ${theme.color('tertiary', 'dark')};
      border-radius: ${theme.getTokens().border.radius.s};
      background-color: ${$isFocused
        ? theme.color('primary')
        : theme.color('white')};

      height: ${size};
      width: ${size};
      min-height: ${size};
      min-width: ${size};

      &:focus,
      &:hover {
        border: ${theme.getTokens().border.width.s} solid ${theme.color('text')};
      }

      &:disabled {
        background-color: ${theme.color('white', 'dark')};
        border-color: ${theme.color('tertiary', 'dark')};
        cursor: auto;

        &:checked {
          background-color: ${theme.color('tertiary', 'light')};
        }
      }

      &:checked {
        /** This is a temporary solution until we make a new checkbox component */
        background-image: ${`url('data:image/svg+xml;utf8,${$iconShown}')`};
        background-repeat: no-repeat;
        background-position: center;
        border: none;

        background-color: ${theme.color($appearance || 'secondary')};
      }
    `;
  }};
`;

export default Checkbox;
